// activity-token-bars.ts — WoW-style timed-activity "cast bar" floating ABOVE a creature token on the
// encounter map. Companion to the action-bar bar (player-character-sheet/tabs/action-bar.ts): both read
// the SAME backend-stamped fields on the gathering/reading/training status entry (activity_kind /
// activity_skill_id / activity_skill_name / activity_start_seconds / activity_end_seconds) and the same
// computed.campaign.campaign_now_seconds clock, so progress is server-authoritative and identical across
// the two surfaces.
//
// ALWAYS-ON (owner 2026-06-30): the bar is shown the whole time a creature is gathering/reading/training,
// NOT hover-gated, so other players can see who's busy + how far along at a glance. We do NOT inject into
// .encounter-creature-overlay because that overlay is overflow:hidden — a bar floated ABOVE the token
// (bottom:100%) would be clipped. Instead we append a sibling into the token's inner .position-relative
// wrapper (no overflow there; .encounter-creature uses `contain:layout`, which does NOT clip paint).
//
// IMPERATIVE + IDEMPOTENT (mirrors ft3d-oa-threat's _decorate): we create/update/remove the bar element on
// each call, so it self-heals after any token re-render. Driven from the WS handlers — caster_update (the
// actor's status start/finish, which also patches the widget.encounterArr the token reads) and
// campaign_update / encounter_update (clock advance + map refresh) — AND by a light self-healing ticker so
// the bar appears on the INITIAL map render (no WS event fires for an already-in-progress activity) and
// survives token re-renders during a static gather (no WS traffic to re-add it otherwise).

import { tokenCreatureArr } from '../encounter-map/token-data';
import { itemsCol } from '../shared/array-types';
import type { StatusEffectInstance } from '../shared/array-types';

// StatusEffect::getId('gathering'|'reading'|'training') (includes/catalogs/StatusEffect.php).
const ACTIVITY_KIND: Record<string, string> = {
    '3F171782-CC56-32DF-81B4-8C0ED89F47F4': 'Gathering',
    'ED30CCB0-1645-B97E-B9A6-867D8D0094BD': 'Reading',
    '426C7E0B-EF44-498C-10B2-B50A9E28E45C': 'Training',
};

function ensureStyle(): void {
    if (typeof document === 'undefined' || document.getElementById('ft-token-activity-bar-css')) return;
    const style = document.createElement('style');
    style.id = 'ft-token-activity-bar-css';
    style.textContent = `
/* ALWAYS visible while the activity is active (owner 2026-06-30) — the bar element only EXISTS when the
   creature is gathering/reading/training (the updater creates/removes it), so other players can see who's
   busy at a glance without hovering. (The rest of the token overlay — HP/AP — stays hover-gated.) */
.ft-token-activity-bar{position:absolute;left:50%;bottom:calc(100% + 4px);transform:translateX(-50%);z-index:140;display:flex;align-items:center;gap:5px;min-width:72px;max-width:180px;padding:2px 7px;border-radius:9px;background:rgba(12,15,20,.95);border:1px solid rgba(255,255,255,.42);box-shadow:0 0 0 1px rgba(0,0,0,.9),0 2px 10px rgba(0,0,0,.8);white-space:nowrap;pointer-events:none;}
.ft-token-activity-bar .ft-tab-ico{flex:0 0 auto;display:inline-flex;align-items:center;gap:3px;line-height:0;}
.ft-token-activity-bar .ft-tab-ico img,.ft-token-activity-bar .ft-tab-ico svg,.ft-token-activity-bar .ft-tab-ico .ft-icon,.ft-token-activity-bar .ft-tab-ico .ft-icon-map{width:16px;height:16px;display:block;filter:drop-shadow(0 1px 1px rgba(0,0,0,.95));}
.ft-token-activity-bar .ft-tab-track{position:relative;flex:1 1 auto;display:flex;height:14px;min-width:46px;border-radius:7px;background:rgba(255,255,255,.16);border:1px solid rgba(0,0,0,.7);overflow:hidden;box-shadow:inset 0 1px 2px rgba(0,0,0,.65);}
.ft-token-activity-bar .ft-tab-fill{height:100%;width:0%;background:linear-gradient(180deg,#9bef93,#2fae35);box-shadow:0 0 7px rgba(86,232,96,.6);transition:width .4s cubic-bezier(.22,.61,.36,1);}
/* Per-activity fill color — tell activities apart at a glance. Gathering keeps the default green. */
.ft-token-activity-bar[data-ft-activity-kind="reading"] .ft-tab-fill{background:linear-gradient(180deg,#7fc9f5,#1f6fb0);box-shadow:0 0 7px rgba(90,185,240,.6);}
.ft-token-activity-bar[data-ft-activity-kind="training"] .ft-tab-fill{background:linear-gradient(180deg,#f6cf63,#c8861a);box-shadow:0 0 7px rgba(240,195,90,.6);}
.ft-token-activity-bar .ft-tab-time{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:800;color:#fff;letter-spacing:.02em;text-shadow:0 1px 2px rgba(0,0,0,1),0 0 3px rgba(0,0,0,.95);pointer-events:none;}
@media (prefers-reduced-motion:reduce){.ft-token-activity-bar .ft-tab-fill{transition:none;}}
`;
    document.head.appendChild(style);
}

/** Remaining seconds → largest-unit human string (h/m/s), e.g. "1h 12m", "12m 30s", "45s". */
function humanizeSeconds(total: number): string {
    let s = Math.max(0, Math.round(total));
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60); const sec = s - m * 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
}

/** Current campaign clock as absolute seconds (computed.campaign.campaign_now_seconds on the active
 *  campaign blob), or NaN. Same basis as the activity status bounds → the bar just subtracts. */
function campaignNowSeconds(): number {
    const ft = window.FateTide;
    const campId = ft?.campaign?.id;
    const camp = campId ? ft?.campaignCache?.get(campId) : null;
    const v = camp?.computed?.campaign?.campaign_now_seconds;
    return v == null ? NaN : Number(v);
}

/** The status effect uuid for a status entry (proven ftItemsCol path, with a structural fallback). */
function statusItemId(st: StatusEffectInstance): string {
    const v = itemsCol<string>(st, 'item_id')
        ?? (st.status_arr?.items as { item_id?: unknown } | undefined)?.item_id
        ?? st.status_item_id;
    return String(v || '');
}

/** The creature's active timed-activity status (gathering/reading/training, target bucket), or null. */
function findActivityStatus(blob: unknown): StatusEffectInstance | null {
    const statuses: unknown = typeof ftCreatureStatuses === 'function'
        ? ftCreatureStatuses(blob, 'target')
        : (blob as { statuses?: { target?: unknown } } | null)?.statuses?.target;
    if (!Array.isArray(statuses)) return null;
    for (const st of statuses) {
        if (st && typeof st === 'object' && !Array.isArray(st)) {
            const status = st as StatusEffectInstance;
            if (ACTIVITY_KIND[statusItemId(status).toUpperCase()]) return status;
        }
    }
    return null;
}

/**
 * Reflow every map token's above-head activity bar from the CURRENT campaign clock + blob statuses.
 * Creates the bar on a token that just started an activity, updates its fill/time, and removes it when
 * the activity ends (or the creature dies). Safe to call on every relevant WS event (idempotent).
 */
export function ftUpdateActivityTokenBars(): void {
    if (typeof document === 'undefined') return;
    const tokens = document.querySelectorAll<HTMLElement>('.encounter-creature');
    if (!tokens.length) return;
    ensureStyle();
    const now = campaignNowSeconds();
    tokens.forEach((tok) => {
        const wrap = tok.querySelector<HTMLElement>(':scope > .position-relative') || tok;
        let bar = wrap.querySelector<HTMLElement>(':scope > .ft-token-activity-bar');
        const dead = tok.classList.contains('encounter-creature-dead');
        const blob = (!dead) ? tokenCreatureArr(tok) : null;
        const st = blob ? findActivityStatus(blob) : null;
        if (!st) { if (bar) bar.remove(); return; }

        const skillId = String(st.activity_skill_id || '');
        const hasSkill = skillId !== '' && checkIfValidUUID(skillId);
        const statusId = statusItemId(st);
        const kindLabel = ACTIVITY_KIND[statusId.toUpperCase()] || 'Working';
        const skillName = String(st.activity_skill_name || '').trim();
        const title = (hasSkill && skillName !== '') ? `${kindLabel}: ${skillName}` : kindLabel;

        if (!bar) {
            bar = document.createElement('div');
            bar.className = 'ft-token-activity-bar';
            bar.innerHTML = '<span class="ft-tab-ico"></span>'
                + '<span class="ft-tab-track"><span class="ft-tab-fill"></span><span class="ft-tab-time"></span></span>';
            wrap.appendChild(bar);
        }
        bar.setAttribute('title', title);
        // Per-activity fill color (CSS, keyed off the kind): gathering=green, reading=blue, training=amber.
        bar.setAttribute('data-ft-activity-kind', ACTIVITY_KIND[statusId.toUpperCase()] ? kindLabel.toLowerCase() : '');

        // Far-left: ACTIVITY icon (status uuid → Reading/Gathering/Training) THEN the specific SKILL icon.
        // Rebuild only when the resolved pair changes (avoids re-mounting the <img>s every clock tick).
        const iconKey = statusId + '|' + (hasSkill ? skillId : '');
        const ico = bar.querySelector<HTMLElement>('.ft-tab-ico');
        if (ico && ico.getAttribute('data-icon-key') !== iconKey) {
            const a = iconMap(statusId, 13, 13);
            const b = hasSkill ? iconMap(skillId, 13, 13) : '';
            ico.innerHTML = a + b;
            ico.setAttribute('data-icon-key', iconKey);
        }

        // Fill (drains) + remaining time. The persistent fill element's width transition glides each tick.
        const fill = bar.querySelector<HTMLElement>('.ft-tab-fill');
        const timeEl = bar.querySelector<HTMLElement>('.ft-tab-time');
        const start = Number(st.activity_start_seconds);
        const end = Number(st.activity_end_seconds);
        if (!fill) return;
        if (!isFinite(start) || !isFinite(end) || end <= start || !isFinite(now)) {
            fill.style.width = '0%';
            if (timeEl) timeEl.textContent = '';
            return;
        }
        const total = end - start;
        const remaining = Math.max(0, Math.min(total, end - now));
        // WoW-style: fill GROWS left→right toward completion (elapsed %); text counts remaining down.
        const pct = Math.max(0, Math.min(100, ((total - remaining) / total) * 100));
        fill.style.width = `${pct.toFixed(2)}%`;
        if (timeEl) timeEl.textContent = humanizeSeconds(remaining);
    });
}

// Self-healing presence ticker. WS events drive immediate updates, but: (a) on initial map render NO WS
// event fires for an already-in-progress activity, and (b) a token re-render can wipe the imperative bar
// during a static gather (campaign clock not advancing → no campaign_update). A light interval re-affirms
// every bar so it's reliably present whenever a creature is active. Cheap: early-returns when no
// `.encounter-creature` tokens exist (non-map pages), else a small per-token resolve; the fill only visibly
// changes when the campaign clock actually moved, so this never flickers. Mirrors the turn-timer ticker.
if (typeof window !== 'undefined') {
    if (!window.__ftActivityTokenBarTicker) {
        window.__ftActivityTokenBarTicker = setInterval(() => {
            try { ftUpdateActivityTokenBars(); } catch { /* no-op */ }
        }, 2000);
    }
}

export { campaignNowSeconds, findActivityStatus, humanizeSeconds, statusItemId };
